<template>
    <div class="model-pairing">
        <div v-if="!productModel" class="empty">请先在基础信息中创建型号</div>
        <template v-else>
            <div class="saved-row">
                <span class="row-label">当前分配</span>
                <template v-if="saved.pairing_model_code">
                    <span class="saved-model">{{ modelName(saved.pairing_model_code) }}</span>
                    <span class="form-tip">{{ saved.scheme_codes.length ? `已选 ${saved.scheme_codes.length} 个方案` : '使用该配网型号下全部方案' }}</span>
                </template>
                <span v-else class="form-tip">未分配，App 按型号前缀匹配</span>
                <el-tag v-if="dirty" size="mini" type="warning">有未保存的修改</el-tag>
            </div>
            <div class="pairing-row">
                <span class="row-label">所属配网型号</span>
                <el-select v-model="pairingModelCode" filterable clearable size="small" placeholder="选择配网型号"
                    style="width: 260px;" @change="onPairingModelChange">
                    <el-option v-for="m in pairingModels" :key="m.code" :label="m.model" :value="m.code">
                    </el-option>
                </el-select>
                <span v-if="suggestModel && !pairingModelCode" class="form-tip">
                    按型号前缀推荐：
                    <el-button type="text" size="small" @click="applySuggest">{{ suggestModel.model }}</el-button>
                </span>
                <div class="row-actions">
                    <el-button size="small" :disabled="!dirty" @click="restore">撤销修改</el-button>
                    <el-button type="primary" size="small" :loading="saving" :disabled="!dirty" @click="save">保存</el-button>
                </div>
            </div>

            <div v-if="!pairingModelCode" class="empty">
                <template v-if="saved.pairing_model_code">
                    保存后将取消分配（原为 {{ modelName(saved.pairing_model_code) }}），App 改为按型号前缀匹配。
                    <el-button type="text" size="small" @click="restore">撤销</el-button>
                </template>
                <template v-else>未分配配网型号时，App 按型号前缀匹配配网型号下的全部方案</template>
            </div>
            <template v-else>
                <div class="form-tip" style="margin-bottom: 8px;">
                    勾选该型号支持的配网方案；不勾选任何方案时，App 使用所属配网型号下的全部方案
                </div>
                <el-table ref="table" :data="schemes" border size="small" row-key="code" v-loading="loading"
                    @selection-change="onSelectionChange">
                    <el-table-column type="selection" width="45" align="center"></el-table-column>
                    <el-table-column label="列表图" width="70" align="center">
                        <template v-slot="scope">
                            <el-image v-if="imageOf(scope.row)" :src="imageOf(scope.row)" fit="contain"
                                class="scheme-thumb"></el-image>
                        </template>
                    </el-table-column>
                    <el-table-column label="方案名称" prop="name" min-width="180" show-overflow-tooltip></el-table-column>
                    <el-table-column label="蓝牙 modelId" width="100" align="center">
                        <template v-slot="scope">
                                <span v-if="scope.row.functions.includes('support_ble') && !scope.row.ble_match" class="form-tip">不匹配</span>
                                <span v-else>{{ modelIdOf(scope.row) || '-' }}</span>
                            </template>
                    </el-table-column>
                    <el-table-column label="配网方式" min-width="200">
                        <template v-slot="scope">
                            <el-tag v-for="f in functionsOf(scope.row, 'connection')" :key="f.code" size="mini"
                                class="fn-tag">{{ f.name }}</el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="设备特性" min-width="200">
                        <template v-slot="scope">
                            <el-tag v-for="f in functionsOf(scope.row, 'feature')" :key="f.code" size="mini" type="info"
                                class="fn-tag">{{ f.name }}</el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="展示" width="110" align="center">
                        <template v-slot="scope">
                            <el-tag v-if="scope.row.test_only" size="mini" type="warning">仅测试</el-tag>
                            <el-tag v-if="scope.row.hide_in_list" size="mini" type="info">隐藏</el-tag>
                            <span v-if="!scope.row.test_only && !scope.row.hide_in_list" class="form-tip">所有用户可见</span>
                        </template>
                    </el-table-column>
                </el-table>
            </template>
        </template>
    </div>
</template>

<script>
import pairingApi from './api'

export default {
    name: 'ModelPairingTab',
    props: {
        // 具体型号的内部型号（client_product_model.model，即设备上报的完整类型，如 GC2_A10S8A5）。
        // 取已保存的值，新建型号时为空
        productModel: { type: String, default: '' },
    },
    data() {
        return {
            functions: [],
            pairingModels: [],
            pairingModelCode: '',
            schemes: [],
            selectedCodes: [],
            // 切换配网型号时记住每个配网型号下勾选的方案，切回来时恢复，不会因为切换或清空而丢失
            selectionByModel: {},
            saved: { pairing_model_code: '', scheme_codes: [] },
            loading: false,
            saving: false,
            syncing: false,
        }
    },
    computed: {
        // 与 App 现有规则一致：具体型号以配网型号名开头，取最长匹配
        suggestModel() {
            const name = (this.productModel || '').toUpperCase()
            if (!name) return null
            return this.pairingModels
                .filter((m) => name.startsWith(m.model.toUpperCase()))
                .sort((a, b) => b.model.length - a.model.length)[0] || null
        },
        dirty() {
            const sorted = (list) => [...list].sort().join(',')
            return this.pairingModelCode !== this.saved.pairing_model_code ||
                sorted(this.selectedCodes) !== sorted(this.saved.scheme_codes)
        },
    },
    watch: {
        productModel: {
            immediate: true,
            handler(code) {
                if (code) this.load()
            },
        },
    },
    created() {
        pairingApi.PairingMeta().then((res) => {
            this.functions = res.data.data.functions
        })
    },
    methods: {
        load() {
            this.loading = true
            Promise.all([
                pairingApi.PairingModelList(),
                pairingApi.PairingProductModelDetail({ product_model: this.productModel }),
            ]).then(([listRes, detailRes]) => {
                this.pairingModels = listRes.data.data
                const data = detailRes.data.data || {}
                this.saved = { pairing_model_code: data.pairing_model_code || '', scheme_codes: data.scheme_codes || [] }
                this.restore()
            }).catch(() => { }).finally(() => {
                this.loading = false
            })
        },
        modelName(code) {
            const m = this.pairingModels.find((v) => v.code === code)
            return m ? m.model : code
        },
        // 回到已保存的分配
        restore() {
            this.selectionByModel = {}
            if (this.saved.pairing_model_code) {
                this.selectionByModel[this.saved.pairing_model_code] = [...this.saved.scheme_codes]
            }
            this.pairingModelCode = this.saved.pairing_model_code
            return this.loadSchemes()
        },
        // 加载当前配网型号的方案，并勾选上次在该配网型号下勾选的方案
        loadSchemes() {
            const code = this.pairingModelCode
            this.selectedCodes = [...(this.selectionByModel[code] || [])]
            if (!code) {
                this.schemes = []
                return Promise.resolve()
            }
            this.syncing = true
            return pairingApi.PairingSchemeList({ pairing_model_code: code }).then((res) => {
                if (code !== this.pairingModelCode) return
                this.schemes = res.data.data
                const checked = this.selectedCodes
                this.$nextTick(() => {
                    const table = this.$refs.table
                    if (table) {
                        table.clearSelection()
                        this.schemes.filter((s) => checked.includes(s.code)).forEach((s) => table.toggleRowSelection(s, true))
                    }
                    this.selectedCodes = checked.filter((c) => this.schemes.some((s) => s.code === c))
                    this.selectionByModel[code] = [...this.selectedCodes]
                    this.syncing = false
                })
            }).catch(() => {
                this.syncing = false
            })
        },
        onPairingModelChange() {
            this.loadSchemes()
        },
        applySuggest() {
            this.pairingModelCode = this.suggestModel.code
            this.loadSchemes()
        },
        onSelectionChange(rows) {
            // 加载和恢复勾选过程中的中间状态不记录
            if (this.syncing) return
            this.selectedCodes = rows.map((r) => r.code)
            if (this.pairingModelCode) this.selectionByModel[this.pairingModelCode] = [...this.selectedCodes]
        },
        imageOf(scheme) {
            const img = scheme.images.find((i) => i.type === 'left') || scheme.images.find((i) => i.type === 'main')
            return img && img.url
        },
        modelIdOf(scheme) {
            return scheme.model_id
        },
        functionsOf(scheme, type) {
            return this.functions.filter((f) => f.type === type && scheme.functions.includes(f.code))
        },
        save() {
            this.saving = true
            pairingApi.PairingProductModelAssign({
                product_model: this.productModel,
                pairing_model_code: this.pairingModelCode || '',
                scheme_codes: this.pairingModelCode ? this.selectedCodes : [],
            }).then((res) => {
                this.saving = false
                if (res.data.code == 200) {
                    this.$message.success('保存成功')
                    this.saved = { pairing_model_code: this.pairingModelCode || '', scheme_codes: this.pairingModelCode ? [...this.selectedCodes] : [] }
                    // 已保存，其他配网型号下暂存的勾选不再需要
                    this.selectionByModel = this.pairingModelCode ? { [this.pairingModelCode]: [...this.selectedCodes] } : {}
                    return
                }
                this.$message.error('保存失败' + res.data.msg)
            })
        },
    },
}
</script>

<style scoped>
.saved-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
    padding: 8px 12px;
    background: #f5f7fa;
    border-radius: 4px;
}

.saved-model {
    font-weight: 600;
    color: #303133;
}

.pairing-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
}

.row-label {
    font-size: 13px;
    color: #606266;
}

.row-actions {
    margin-left: auto;
}

.form-tip {
    font-size: 12px;
    line-height: 18px;
    color: #909399;
}

.scheme-thumb {
    width: 40px;
    height: 40px;
}

.fn-tag {
    margin: 2px 4px 2px 0;
}

.empty {
    padding: 40px 0;
    text-align: center;
    font-size: 13px;
    color: #c0c4cc;
}
</style>
